import { FunctionComponent, useEffect, useState } from 'react'
import moment from 'moment-timezone'
import { BookRange, Daycare } from '@/types/Daycare'
import { SelectOptionsDeprecated } from '@/types/Select'
import styles from './daycareMergerSettings.module.scss'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useMutation } from '@apollo/client'
import { CHANGE_MIN_SLOT } from '@/modules/API/daycare/daycareHandler'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface DaycareMergerSettingsProps {
    daycare: Daycare
    disabled: boolean
}

const DaycareMergerSettings: FunctionComponent<DaycareMergerSettingsProps> = ({
    daycare,
    disabled
}: DaycareMergerSettingsProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newSlotDuration, setNewSlotDuration] = useState<string>(daycare.bookRange)
    const [newSlotsMin, setNewSlotsMin] = useState<number>(daycare.bookMin.valueOf())
    const [newSlotsWeeklyLimit, setNewSlotsWeeklyLimit] = useState<number>(daycare.bookWeeklyLimit.valueOf())

    const [slotsDuration, setSlotsDuration] = useState<string>(daycare.bookRange)
    const [slotsMin, setSlotsMin] = useState<number>(daycare.bookMin.valueOf())
    const [slotsWeeklyLimit, setSlotsWeeklyLimit] = useState<number>(daycare.bookWeeklyLimit.valueOf())

    const [changeMinSlot] = useMutation(CHANGE_MIN_SLOT, {
        variables: {
            daycareId: daycare.id,
            bookWeeklyLimit: slotsWeeklyLimit,
            range: slotsDuration,
            bookMin: slotsMin
        },
        onCompleted: (): void => {
            fetchCurrentDaycare()

            setNewSlotDuration(slotsDuration)
            setNewSlotsMin(slotsMin)
            setNewSlotsWeeklyLimit(slotsWeeklyLimit)

            showNotificationSuccess(t('reservationSettingsUpdated'))
        },
        onError: (): void => {
            showNotificationError(t('errorUpdatingReservationSettings'))
        }
    })

    const slotsDurations: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: t('quarterHour'),
                    value: 'range_15'
                },
                {
                    name: t('halfHour'),
                    value: 'range_30'
                },
                {
                    name: t('hour'),
                    value: 'range_60'
                }
            ]
        }
    ]

    useEffect(() => {
        if (slotsMin < 0) {
            setSlotsMin(0)
        } else if (slotsMin > 8) {
            setSlotsMin(8)
        }
    }, [slotsMin])

    useEffect(() => {
        if (slotsWeeklyLimit > 168) {
            setSlotsWeeklyLimit(168)
        }
        if (slotsWeeklyLimit < 0) {
            setSlotsWeeklyLimit(0)
        }
    }, [slotsWeeklyLimit])

    function changeSlotsWeeklyLimit(value: string): void {
        setSlotsWeeklyLimit(Number(value))
    }

    function changeSlotsMin(value: string): void {
        const newValue: number = Number(value)
        if (slotsMin < newValue * 2) {
            setSlotsMin(slotsMin + 1)
        } else {
            setSlotsMin(slotsMin - 1)
        }
    }

    function checkWorkingHours(): boolean {
        if (Number(moment(daycare.opening.monOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        } else if (Number(moment(daycare.opening.tueOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        } else if (Number(moment(daycare.opening.wedOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        } else if (Number(moment(daycare.opening.thuOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        } else if (Number(moment(daycare.opening.friOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        } else if (Number(moment(daycare.opening.satOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        } else if (Number(moment(daycare.opening.sunOpen, 'hhmm').format('mm')) % Number(slotsDuration.split('_')[1])) {
            return true
        }
        return false
    }

    function cancelValues(): void {
        setSlotsWeeklyLimit(daycare.bookWeeklyLimit.valueOf())
        setSlotsDuration(daycare.bookRange)
        setSlotsMin(daycare.bookMin.valueOf())
    }

    useEffect(cancelValues, [daycare])

    const changed: boolean =
        newSlotsMin !== slotsMin || newSlotsWeeklyLimit !== slotsWeeklyLimit || newSlotDuration !== slotsDuration

    const valid: boolean =
        slotsMin >= 0 &&
        slotsMin <= 8 &&
        slotsWeeklyLimit >= 2 &&
        slotsWeeklyLimit <= 168 &&
        (slotsDuration === BookRange.range15 ||
            slotsDuration === BookRange.range30 ||
            slotsDuration === BookRange.range60)

    return (
        <Table className={styles.SettingsTable}>
            <TableBody>
                <TableRow>
                    <TableCell className={styles.RowText}>{t('slotsRange')}</TableCell>
                    <TableCell>
                        <SelectUnique
                            selectOptions={slotsDurations}
                            selectValue={slotsDuration}
                            setSelectValue={setSlotsDuration}
                            readOnly={disabled}
                            formClassName={styles.UserInput}
                        />
                        {checkWorkingHours() && (
                            <ErrorMessage className={styles.ErrorText} error={t('unsupportedWorkingHours')} />
                        )}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell className={styles.RowText}>{t('slotsMin')}</TableCell>
                    <TableCell>
                        <UserInput
                            type='number'
                            value={(slotsMin / 2).toString()}
                            disabled={disabled}
                            setValue={changeSlotsMin}
                            endAdornment={`${slotsMin > 3 ? t('hours') : t('hour')}`}
                            className={styles.UserInput}
                            minNumber={0}
                            maxNumber={4}
                            verify={'number'}
                        />
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell className={styles.RowText}>{t('slotsWeeklyLimit')}</TableCell>
                    <TableCell>
                        <UserInput
                            type='number'
                            value={slotsWeeklyLimit.toString()}
                            disabled={disabled}
                            setValue={changeSlotsWeeklyLimit}
                            endAdornment={`${slotsWeeklyLimit > 1 ? t('hours') : t('hour')}`}
                            className={styles.UserInput}
                            verify={'number'}
                            minNumber={2}
                            maxNumber={168}
                        />
                    </TableCell>
                </TableRow>
                {!disabled && (
                    <TableRow>
                        <TableCell align={'center'} colSpan={2} className={styles.HideBorder}>
                            <ButtonTextIcon
                                type={'update'}
                                onClick={changeMinSlot}
                                disabled={!changed || !valid}
                                className={styles.Button}
                            />
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    )
}

export default DaycareMergerSettings
